import {
  Button,
  ButtonGroup,
  Classes,
  Dialog,
  IconName,
  Intent,
  Menu,
  MenuDivider,
  MenuItem
} from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import React from "react";
import { useFullScreenBrowser } from "react-browser-hooks";
import { GiKeyboard } from "react-icons/gi";

import { allColorThemes, ColorTheme } from "../ColorTheme";
import { DEMO_MODE, DEMO_MODE_DISABLED_TEXT } from "../DemoMode";
import styles from "./Toolbar.module.css";

export interface ToolbarProps {
  currentColorTheme: ColorTheme;
  onRefreshFileTreeViewClick: () => void;
  onNewFileClick: () => void;
  onSaveClick: () => void;
  onColorThemeClick: (colorTheme: ColorTheme) => void;
  onUndoClick: () => void;
  onRedoClick: () => void;
  onCutClick: () => void;
  onCopyClick: () => void;
  onPasteClick: () => void;
}

interface ToolbarMenuProps {
  text: string;
  icon: IconName;
  children: React.ReactNode;
}

function ToolbarMenu(props: ToolbarMenuProps): JSX.Element {
  return (
    <Popover2 content={<Menu>{props.children}</Menu>} placement="bottom-start" minimal={true}>
      <Button
        icon={props.icon}
        rightIcon="caret-down"
        intent={Intent.NONE}
        text={props.text}
        minimal={true}
      />
    </Popover2>
  );
}

function Toolbar(props: ToolbarProps): JSX.Element {
  const [helpAboutDialogOpen, setHelpAboutDialogOpen] = React.useState(false);
  const [openFullScreenRequested, setOpenFullScreenRequested] = React.useState(false);
  const [closeFullScreenRequested, setCloseFullScreenRequested] = React.useState(false);

  const { fullScreen } = useFullScreenBrowser();

  if (fullScreen && openFullScreenRequested) {
    setOpenFullScreenRequested(false);
  }

  if (!fullScreen && closeFullScreenRequested) {
    setCloseFullScreenRequested(false);
  }

  return (
    <>
      <ButtonGroup>
        <ToolbarMenu text="File" icon="document">
          <MenuItem
            text="New File"
            label="Ctrl+N"
            icon="document"
            disabled={DEMO_MODE}
            title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            onClick={props.onNewFileClick}
          />
          <MenuItem
            text="Close Editor"
            icon="add-to-folder"
            disabled={DEMO_MODE}
            title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
          />
          <MenuDivider />
          <MenuItem text="Save" label="Ctrl+S" icon="floppy-disk" onClick={props.onSaveClick} />
          <MenuItem
            text="Save as..."
            icon="floppy-disk"
            disabled={DEMO_MODE}
            title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
          />
          <MenuItem text="Save All" icon="floppy-disk" onClick={props.onSaveClick} />
          <MenuDivider />
          <MenuItem
            text="Refresh File Browser"
            icon="refresh"
            onClick={props.onRefreshFileTreeViewClick}
          />
          <MenuDivider />
          <MenuItem
            text="Upload File"
            icon="upload"
            disabled={DEMO_MODE}
            title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
          />
          <MenuItem text="Download Workspace" icon="cloud-download">
            <MenuItem
              text="as ZIP file"
              icon="compressed"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
            <MenuItem
              text="as tar.gz file"
              icon="compressed"
              disabled={DEMO_MODE}
              title={DEMO_MODE ? DEMO_MODE_DISABLED_TEXT : undefined}
            />
          </MenuItem>
        </ToolbarMenu>

        <ToolbarMenu text="Edit" icon="draw">
          <MenuItem text="Undo" label="Ctrl+Z" icon="undo" onClick={props.onUndoClick} />
          <MenuItem text="Redo" label="Ctrl+Y" icon="redo" onClick={props.onRedoClick} />
          <MenuDivider />
          <MenuItem text="Cut" label="Ctrl+X" icon="cut" onClick={props.onCutClick} />
          <MenuItem text="Copy" label="Ctrl+C" icon="duplicate" onClick={props.onCopyClick} />
          <MenuItem text="Paste" label="Ctrl+V" icon="clipboard" onClick={props.onPasteClick} />
        </ToolbarMenu>

        <ToolbarMenu text="View" icon="mountain">
          <MenuItem
            text="Full Screen"
            label="F11"
            icon={fullScreen ? "small-tick" : "desktop"}
            active={fullScreen}
            onClick={() =>
              fullScreen ? setCloseFullScreenRequested(true) : setOpenFullScreenRequested(true)
            }
          />
          <MenuDivider title="Color Theme" />
          {allColorThemes().map((colorTheme) => (
            <MenuItem
              key={colorTheme}
              text={colorTheme}
              active={colorTheme === props.currentColorTheme}
              icon={colorTheme === props.currentColorTheme ? "tick-circle" : "circle"}
              onClick={() => props.onColorThemeClick(colorTheme)}
            />
          ))}
        </ToolbarMenu>

        <ToolbarMenu text="Help" icon="help">
          <MenuItem
            text="About HexGrip"
            icon="info-sign"
            onClick={() => setHelpAboutDialogOpen(true)}
          />
        </ToolbarMenu>
      </ButtonGroup>

      <OpenFullScreenDialog
        isOpen={openFullScreenRequested && !fullScreen}
        onCancel={() => setOpenFullScreenRequested(false)}
      />

      <CloseFullScreenDialog
        isOpen={closeFullScreenRequested && fullScreen}
        onCancel={() => setCloseFullScreenRequested(false)}
      />

      <HelpAboutDialog isOpen={helpAboutDialogOpen} onClose={() => setHelpAboutDialogOpen(false)} />
    </>
  );
}

interface HelpAboutDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

function HelpAboutDialog(props: HelpAboutDialogProps): JSX.Element {
  return (
    <Dialog isOpen={props.isOpen} onClose={props.onClose} title="About HexGrip" icon="info-sign">
      <div className={Classes.DIALOG_BODY}>
        <p>
          <strong>Welcome and good luck.</strong>
        </p>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button intent={Intent.PRIMARY} text="OK" onClick={props.onClose} />
        </div>
      </div>
    </Dialog>
  );
}

interface OpenFullScreenDialogProps {
  isOpen: boolean;
  onCancel: () => void;
}

function OpenFullScreenDialog(props: OpenFullScreenDialogProps): JSX.Element {
  return (
    <Dialog isOpen={props.isOpen} onClose={props.onCancel} title="Enter Full Screen" icon="desktop">
      <RequestFullScreenDialogContents onCancel={props.onCancel}>
        Press the{" "}
        <strong>
          <code>F11</code>
        </strong>{" "}
        key to enter Full Screen mode
      </RequestFullScreenDialogContents>
    </Dialog>
  );
}

interface CloseFullScreenDialogProps {
  isOpen: boolean;
  onCancel: () => void;
}

function CloseFullScreenDialog(props: CloseFullScreenDialogProps): JSX.Element {
  return (
    <Dialog isOpen={props.isOpen} onClose={props.onCancel} title="Exit Full Screen" icon="minimize">
      <RequestFullScreenDialogContents onCancel={props.onCancel}>
        Press the{" "}
        <strong>
          <code>F11</code>
        </strong>{" "}
        key to exit Full Screen mode
      </RequestFullScreenDialogContents>
    </Dialog>
  );
}

interface RequestFullScreenDialogContentsProps {
  onCancel: () => void;
  children: React.ReactNode;
}

function RequestFullScreenDialogContents(props: RequestFullScreenDialogContentsProps): JSX.Element {
  return (
    <>
      <div
        className={classNames([
          Classes.DIALOG_BODY,
          Classes.RUNNING_TEXT,
          styles.fullscreen_dialog_cont
        ])}
      >
        <p>
          <GiKeyboard size="164px" />
        </p>
        <p>{props.children}</p>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button intent={Intent.NONE} text="Cancel" onClick={props.onCancel} />
        </div>
      </div>
    </>
  );
}

export default Toolbar;
